import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { PureComponent } from "react";
import { connect } from "react-redux";
import type { FormatOptionLabelMeta } from "react-select";
import { formValueSelector, type InjectedFormProps, reduxForm } from "redux-form";

import { simpleDropdownFieldValidation, simpleDropdownMultiSelectFieldValidation } from "@/app-utils/form.ts";
import { type SampleStatus, type SampleType } from "@/graphql.ts";
import { type IListingState, loadSampleTypes } from "@/redux/modules/listing.ts";
import { type IAppState } from "@/redux/reducer.ts";

import { type IGenericCardConfiguration } from "../../redux/modules/dashboard.js";
import GenericCardEditingDialog, { type IGenericCardEditingProps } from "../core/GenericCardEditingDialog.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { type ISampleStatusesCardConfig } from "./SampleStatusDashboardCard.js";

type ISampleStatusDashboardCardEditingFormOwnProps = IGenericCardEditingProps;

interface ISampleStatusDashboardCardEditingFormAppStateProps {
  sampleType: SampleType;
  selectedStatuses: SampleStatus[];
  initialValues: Partial<FormData>;
  listingState: IListingState;
}

interface ISampleStatusDashboardCardEditingFormDispatchProps {
  loadSampleTypes: () => Promise<SampleType[]>;
}

export interface ISampleStatusDashboardCardEditingFormProps
  extends ISampleStatusDashboardCardEditingFormAppStateProps,
    ISampleStatusDashboardCardEditingFormDispatchProps,
    ISampleStatusDashboardCardEditingFormOwnProps,
    InjectedFormProps<FormData, any> {
  // only if this screen is a redux-form
}

function mapStateToProps(
  state: IAppState,
  ownProps: ISampleStatusDashboardCardEditingFormProps,
): ISampleStatusDashboardCardEditingFormAppStateProps {
  const selectedSampleType: SampleType = state.listing.sampleTypes.find(
    (s: SampleType) => s.id === (ownProps.initialConfig.specificConfig as ISampleStatusesCardConfig).sampleTypeId,
  );

  // if selection exists, use it, otherwise take all statuses of the sample type
  const initialStatuses: SampleStatus[] = (
    ownProps.initialConfig.specificConfig as ISampleStatusesCardConfig
  ).statusIds.map((i) => selectedSampleType?.sampleStatuses.find((s) => s.id === i));

  return {
    sampleType: formValueSelector(SampleStatusDashboardCardEditingForm.FORM_NAME)(
      state,
      SampleStatusDashboardCardEditingForm.FIELD_SAMPLE_TYPE,
    ),
    selectedStatuses: formValueSelector(SampleStatusDashboardCardEditingForm.FORM_NAME)(
      state,
      SampleStatusDashboardCardEditingForm.FIELD_STATUSES,
    ),
    initialValues: {
      [GenericCardEditingDialog.CARD_EDITING_DIALOG_CARD_TITLE]: ownProps.initialConfig.title,
      [SampleStatusDashboardCardEditingForm.FIELD_SAMPLE_TYPE]: selectedSampleType,
      [SampleStatusDashboardCardEditingForm.FIELD_STATUSES]: initialStatuses,
    },
    listingState: state.listing,
  };
}

function mapDispatchToProps(dispatch): ISampleStatusDashboardCardEditingFormDispatchProps {
  return {
    loadSampleTypes: () => dispatch(loadSampleTypes()),
  };
}

class SampleStatusDashboardCardEditingForm extends PureComponent<ISampleStatusDashboardCardEditingFormProps> {
  public static FORM_NAME = "SAMPLE_STATUS_CARD_EDITING_FORM";
  public static FIELD_SAMPLE_TYPE = "SAMPLE_TYPE";
  public static FIELD_STATUSES = "STATUSES";

  constructor(props) {
    super(props);
  }

  componentDidMount() {
    this.props.loadSampleTypes();
  }

  onSampleTypeChanged = (sampleType: SampleType) => {
    // Derive a new default name after every sample type change.
    // Note that the sample type name has up to 150 chars while the title may not be longer thn 50 chars.
    const trailerText = " Status Summary";
    const maxTitleLength: number = DB_FIELD_MAX_LENGTH.CARD.TITLE;
    const sampleName: string = sampleType.name;
    let title = "";
    if (sampleName.length + trailerText.length <= maxTitleLength) {
      title = sampleName + trailerText;
    } else if (sampleName.length <= maxTitleLength) {
      title = sampleName;
    } else {
      title = sampleName.substring(0, maxTitleLength - 3) + "...";
    }

    this.props.change(GenericCardEditingDialog.CARD_EDITING_DIALOG_CARD_TITLE, title);
    // filter out the statuses that may not apply anymore
    const cleanedStatuses: SampleStatus[] =
      this.props.selectedStatuses.length > 0
        ? this.props.selectedStatuses.filter((e) => sampleType.sampleStatuses.find((s) => s.id === e.id))
        : sampleType.sampleStatuses;
    this.props.change(SampleStatusDashboardCardEditingForm.FIELD_STATUSES, cleanedStatuses);
  };

  handleSubmitData = (values) => {
    const cardTitle: string = values[GenericCardEditingDialog.CARD_EDITING_DIALOG_CARD_TITLE];
    const sampleType: SampleType = values[SampleStatusDashboardCardEditingForm.FIELD_SAMPLE_TYPE];
    const sampleStatuses: SampleStatus[] = values[SampleStatusDashboardCardEditingForm.FIELD_STATUSES];

    const configuration: IGenericCardConfiguration = {
      ...this.props.initialConfig,
      title: cardTitle,
      specificConfig: {
        sampleTypeId: sampleType?.id,
        statusIds: sampleStatuses
          .sort((a, b) => a.statusClass.id - b.statusClass.id || a.sortOrder - b.sortOrder)
          .map((s: SampleStatus) => s.id),
      },
    };

    this.props.saveCardConfig(configuration); // local saving
    this.props.onClose();
  };

  render() {
    return (
      <ModalControlledOverlay close={this.props.onClose} isOpen={true}>
        {({ close }) => (
          <GenericCardEditingDialog {...this.props} handleSubmitData={this.handleSubmitData} onClose={close}>
            <div className={"dashboard-card-editing-form-section"} style={{ minWidth: "350px" }}>
              <div className="form-group">
                <label className="control-label">{"Select sample type"}</label>
                <div style={{ padding: "0 5px" }}>
                  <ReactSelectReduxFormField
                    name={SampleStatusDashboardCardEditingForm.FIELD_SAMPLE_TYPE}
                    horizontalLayout={false}
                    isClearable={false}
                    isSearchable={true}
                    tooltip={
                      this.props.listingState.isLoadingSampleTypes
                        ? "Loading sample types..."
                        : "The statuses of the selected sample sample type will be shown on the card"
                    }
                    placeholder={
                      this.props.listingState.isLoadingSampleTypes
                        ? "Loading sample types..."
                        : "Please select a sample type..."
                    }
                    component={ReactSelectField}
                    getOptionValue={(option: SampleType) => option.id}
                    getOptionLabel={(option: SampleType) => option.name}
                    options={this.props.listingState.sampleTypes}
                    isLoading={this.props.listingState.isLoadingSampleTypes}
                    handleChange={this.onSampleTypeChanged}
                    validate={simpleDropdownFieldValidation}
                  />
                </div>
                {this.props.sampleType?.sampleStatuses && (
                  <ReactSelectReduxFormField
                    name={SampleStatusDashboardCardEditingForm.FIELD_STATUSES}
                    isMulti
                    closeMenuOnSelect={false}
                    isClearable={false}
                    isSearchable={true}
                    getOptionValue={(option: SampleStatus) => option.id}
                    getOptionLabel={(option: SampleStatus) => `${option.name} ("${option.statusClass.name}" class)`}
                    formatOptionLabel={(option: SampleStatus, labelMeta: FormatOptionLabelMeta<SampleStatus>) => {
                      const isSelection: boolean = labelMeta.context === "value";
                      return isSelection ? option.name : `${option.name} ("${option.statusClass.name}" class)`;
                    }}
                    label={"Statuses to show"}
                    tooltip={"Select the statuses that should be shown on the status card..."}
                    placeholder={"Select one or multiple statuses..."}
                    component={ReactSelectField}
                    options={this.props.sampleType.sampleStatuses}
                    validate={simpleDropdownMultiSelectFieldValidation}
                  />
                )}
              </div>
            </div>
          </GenericCardEditingDialog>
        )}
      </ModalControlledOverlay>
    );
  }
}

export default connect<
  ISampleStatusDashboardCardEditingFormAppStateProps,
  ISampleStatusDashboardCardEditingFormDispatchProps,
  ISampleStatusDashboardCardEditingFormOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: SampleStatusDashboardCardEditingForm.FORM_NAME,
    enableReinitialize: true,
  })(SampleStatusDashboardCardEditingForm as any) as any,
);
